// LockedPropertyCard — what a viewer sees when they click a locked property.
//
// This is the upgrade moment. It has to do two contradictory things well:
// prove the inventory is real (so the upgrade is worth something) while
// giving away nothing actionable (so the upgrade is still necessary).
//
// Everything rendered here comes from the server's LockedProperty payload.
// There is no blurred-real-value trick: the exact figures were never sent, so
// there is nothing in the DOM or the network tab to recover.

import React from "react";
import { LockedProperty } from "../types";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { statusMeta } from "../lib/statusMeta";
import "../App.css";

interface LockedPropertyCardProps {
  property: LockedProperty;
  onClose: () => void;
  onUpgrade: () => void;
}

const LockedPropertyCard: React.FC<LockedPropertyCardProps> = ({
  property,
  onClose,
  onUpgrade,
}) => {
  const meta = statusMeta(property.status);
  // City + zip only — the street address is never sent for a locked row.
  const place = [property.city, property.state].filter(Boolean).join(", ");

  return (
    <div className="auth-backdrop" onClick={onClose}>
      <div
        className="locked-card"
        onClick={(e) => e.stopPropagation()}
        ref={useFocusTrap<HTMLDivElement>({ onEscape: onClose })}
        role="dialog"
        aria-modal="true"
        aria-labelledby="locked-card-title"
      >
        <button className="auth-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <div className="locked-card-badge" aria-hidden="true">
          🔒
        </div>

        <h2 className="locked-card-title" id="locked-card-title">
          {place || "This property"} {property.zipCode}
        </h2>

        <p className="locked-card-sub">
          Exact address available on Scout and Pro
        </p>

        <dl className="locked-card-facts">
          {property.propertyType && (
            <div>
              <dt>Type</dt>
              <dd>{property.propertyType}</dd>
            </div>
          )}
          <div>
            <dt>Status</dt>
            <dd>
              <span
                className="locked-card-status"
                style={{ backgroundColor: meta.color }}
              >
                {meta.label}
              </span>
            </dd>
          </div>
          {property.valueBucket && (
            <div>
              <dt>Estimated value</dt>
              <dd>{property.valueBucket}</dd>
            </div>
          )}
        </dl>

        {/* Named explicitly so the gap between free and paid is legible,
            rather than leaving the user to guess what they'd be buying. */}
        <p className="locked-card-withheld">
          Upgrade to see the exact address, assessed value, equity, owner
          details and full auction timeline.
        </p>

        <button className="upgrade-btn-primary" onClick={onUpgrade}>
          Upgrade to view
        </button>
      </div>
    </div>
  );
};

export default LockedPropertyCard;
